<template>
  <div class="cs-root sc-page-root">
    <FiPageRail title="Recognition Candidates" class="cs-rail">
      <template #actions>
        <coaching-portal-help />
      </template>
    </FiPageRail>

    <div v-if="actingTenantDiffers" class="cs-tenant-note">
      <i class="fa fa-building-o" aria-hidden="true"></i>
      Viewing <b>{{ actingTenantLabel }}</b> — not your selected fleet
    </div>

    <div v-if="flaggedLoading && !recognitionCandidates.length && !flaggedError" class="text-center" style="margin: 50px;">
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>
    <div v-else-if="flaggedError" class="text-center text-danger" style="margin: 50px;">
      <p>{{ flaggedError }}</p>
      <b-button size="sm" variant="outline-secondary" @click="loadFlaggedDrivers()">Retry</b-button>
    </div>

    <div v-else :class="{ 'cs-stale': flaggedLoading, 'cs-filters-collapsed': !filtersExpanded }">
      <!-- search/filter bar in the Coaching Tickets vocabulary. the Period
           months re-query the scorecard window; the rest filters the rows. -->
      <!-- mobile-only filters toggle (the ERM Event Log pattern, ops_desk.vue):
           collapses every filter control behind one tap so rows reach above
           the fold on a phone; the search pill stays visible. the badge
           counts active filter dimensions (search excluded — it has its own
           always-on field). display:none above 600px, so desktop is
           unaffected. -->
      <button type="button" class="cs-filter-toggle cs-mobile-only"
              :aria-expanded="filtersExpanded ? 'true' : 'false'"
              @click="filtersExpanded = !filtersExpanded">
        <i class="fa fa-sliders" aria-hidden="true"></i>
        <span>Filters</span>
        <span v-if="activeFilterCount" class="cs-filter-badge">{{ activeFilterCount }}</span>
        <i class="fa" :class="filtersExpanded ? 'fa-angle-up' : 'fa-angle-down'" aria-hidden="true"></i>
      </button>
      <div class="cs-filter-row" role="search" aria-label="Filter recognition candidates">
        <!-- one-tap period presets: the scorecard window is month-granular,
             so Nd selects the months covering the last N days. tapping the
             active chip returns to the default window. -->
        <span class="cs-filter-field" role="group" aria-label="Period covering the last">
          <button v-for="p in periodPresets" :key="'pp-' + p" type="button"
                  :class="['cs-preset-chip', { 'cs-preset-chip--active': periodPresetActive(p) }]"
                  :aria-pressed="periodPresetActive(p) ? 'true' : 'false'"
                  @click="setPeriodPreset(p)">{{ p }}d</button>
        </span>
        <span class="cs-filter-field">
          <span class="cs-filter-lbl">Period from</span>
          <b-form-input v-model="scorecardFrom" type="month" size="sm"
                        :class="['cs-filter-month', { 'cs-month-ghost': !scorecardFrom }]"
                        @change="onPeriodChange" />
        </span>
        <span class="cs-filter-field">
          <span class="cs-filter-lbl">To</span>
          <b-form-input v-model="scorecardTo" type="month" size="sm"
                        :class="['cs-filter-month', { 'cs-month-ghost': !scorecardTo }]"
                        @change="onPeriodChange" />
        </span>
        <span class="cs-filter-field">
          <span class="cs-filter-lbl">Reason</span>
          <b-form-select v-model="recognitionReason" :options="reasonFilterOptions" size="sm" class="cs-filter-select" />
        </span>
        <span class="cs-filter-field">
          <span class="cs-filter-lbl">Last recognized</span>
          <b-form-select v-model="recognitionRecency" :options="recencyFilterOptions" size="sm" class="cs-filter-select cs-filter-select--narrow" />
        </span>
        <span class="cs-search">
          <i class="fa fa-search cs-search-icon" aria-hidden="true"></i>
          <input v-model="recognitionSearch" type="text" class="cs-search-input"
                 placeholder="Search driver or reason"
                 aria-label="Search recognition candidates by name or reason" />
          <button v-if="recognitionSearch" type="button" class="cs-search-clear" aria-label="Clear search"
                  @click="recognitionSearch = ''"><i class="fa fa-times" aria-hidden="true"></i></button>
        </span>
      </div>
      <div class="cs-results-row">
        <span class="cs-results-count">
          {{ recognitionRowsFiltered.length === recognitionRows.length
            ? `${recognitionRows.length} candidate${recognitionRows.length === 1 ? '' : 's'}`
            : `${recognitionRowsFiltered.length} of ${recognitionRows.length} candidates` }}
          · {{ scorecardPeriodLabel }}
        </span>
        <button v-if="filtersDirty" type="button" class="cs-clear-filters" @click="clearFilters">
          <i class="fa fa-times" aria-hidden="true"></i>&nbsp;Reset filters
        </button>
      </div>

      <b-card class="cs-panel mt-2" no-body>
        <div class="cs-panel-head">
          The positive mirror of the flags <span class="cs-hint">from the Scorecard · {{ scorecardPeriodLabel }}</span>
        </div>
        <div class="cs-panel-body">
          <div v-if="!recognitionRowsFiltered.length" class="cs-small text-muted">
            <span v-if="!recognitionCandidates.length">Nobody stands out this period — a bigger active roster helps.</span>
            <span v-else>
              No candidates match these filters.
              <b-button size="sm" variant="outline-secondary" class="ml-2" @click="clearFilters">Reset filters</b-button>
            </span>
          </div>
          <div v-else class="cs-table-inline">
            <b-table :items="recognitionRowsPage" :fields="recognitionFields"
                     :sort-by.sync="recognitionSortBy" :sort-desc.sync="recognitionSortDesc"
                     no-local-sorting small responsive show-empty>
              <template slot="driver_name" slot-scope="data">
                <b>{{ data.item.driver_name }}</b>
              </template>
              <template slot="score" slot-scope="data">
                <span class="cs-list-num">{{ data.item.score == null ? '—' : data.item.score }}</span>
                <span class="cs-small text-muted"> {{ data.item.tier }}</span>
              </template>
              <template slot="reason_count" slot-scope="data">
                <span v-for="r in data.item.reasons" :key="r.key"
                      class="cs-flag-chip cs-flag-chip--positive" :title="r.note">{{ r.label }}</span>
              </template>
              <template slot="miles" slot-scope="data">
                <span class="cs-list-num">{{ data.item.miles == null ? '—' : data.item.miles.toLocaleString() }}</span>
              </template>
              <template slot="days_since_recognized_sort" slot-scope="data">
                <span v-if="data.item.last_recognized" class="cs-list-num">
                  {{ data.item.days_since_recognized }}d ago
                </span>
                <span v-else class="cs-positive font-weight-bold">never — start here</span>
              </template>
              <template slot="actions" slot-scope="data">
                <b-button v-if="!data.item.queued" size="sm" variant="outline-success"
                          @click.stop="openRecognize(data.item.rc)">Recognize</b-button>
                <b-badge v-else variant="light" title="Already in the recognition lane">queued</b-badge>
              </template>
            </b-table>
            <div v-if="recognitionRowsFiltered.length > perPage" class="cs-pager">
              <b-pagination v-model="recognitionPage" :total-rows="recognitionRowsFiltered.length"
                            :per-page="perPage" size="sm" class="mb-0" />
              <span class="cs-small text-muted">{{ recognitionRowsFiltered.length }} drivers</span>
            </div>
          </div>
        </div>
      </b-card>

      <p class="cs-small text-muted mt-3">
        Elite-tier drivers and the fleet's single best performer on each coached metric.
        Coaching that only ever corrects reads as punishment — recognition lives in its own lane
        and never counts toward the coaching workload numbers. The default sort puts drivers
        never recognized first: that's the gap worth closing today.
      </p>
    </div>

    <!-- recognize modal (mixin owns the flow) -->
    <b-modal id="cs-recognize-modal" hide-footer :title="recognizeDriver ? 'Recognize — ' + recognizeDriver.driver.driver_name : ''">
      <div v-if="recognizeDriver">
        <div class="cs-label">Reason</div>
        <b-form-radio-group v-model="recognizeReasonKey" stacked class="mb-2">
          <b-form-radio v-for="r in recognizeDriver.reasons" :key="r.key" :value="r.key"
                        :disabled="isRecognitionQueued(recognizeDriver.driver, r.label)">
            {{ r.label }}
            <span class="cs-small text-muted"> — {{ r.note }}</span>
            <b-badge v-if="isRecognitionQueued(recognizeDriver.driver, r.label)" variant="light" class="ml-1">already queued</b-badge>
          </b-form-radio>
        </b-form-radio-group>

        <div class="cs-label">Note <span class="cs-hint">what you'd say out loud — this rides on the record</span></div>
        <b-form-textarea v-model="recognizeWhy" rows="2" class="mb-3" />

        <!-- who delivers it — same picker as the Assign modal; the person
             spotting the numbers isn't always the one who says it -->
        <div class="cs-label">Assign to</div>
        <b-form-select v-model="recognizeTo" :options="assignContactOptions" size="sm" class="mb-3 cs-recognize-select" />

        <div class="cs-small text-muted mb-3">
          Creates a recognition record for {{ recognizeDriver.driver.driver_name }} with the
          {{ scorecardPeriodLabel }} numbers attached. It sits in its own lane — recognition
          never counts toward the coaching workload. Sign it off once it's been delivered.
          <span v-if="recognizeTo && recognizeTo !== user.username">
            The assignee gets a notification that it landed on them.
          </span>
        </div>

        <div class="d-flex justify-content-end">
          <b-button variant="outline-secondary" class="mr-2" @click="$bvModal.hide('cs-recognize-modal')">Cancel</b-button>
          <b-button variant="success" :disabled="!recognizeReasonKey || recognizeSaving" @click="saveRecognize">
            {{ recognizeSaving ? 'Assigning…' : 'Assign' }}
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import moment from 'moment'
import coachingScopeMixin from '../mixins/coaching_scope_mixin'
import CoachingPortalHelp from './coaching_portal_help.vue'

export default {
  name: 'coaching_recognition',
  components: { CoachingPortalHelp },
  mixins: [coachingScopeMixin],
  data() {
    return {
      perPage: 15,
      periodPresets: [7, 30, 90],
      filtersExpanded: false,
      recognitionSearch: '',
      recognitionReason: '',
      recognitionRecency: '',
      recognitionPage: 1,
      recognitionSortBy: 'days_since_recognized_sort',
      recognitionSortDesc: true,
      // days_since_recognized buckets; "never" is the gap worth closing, so
      // the gap options include it
      recencyFilterOptions: [
        { value: '', text: 'Any' },
        { value: 'never', text: 'Never recognized' },
        { value: 'gap90', text: 'Not in 90+ days' },
        { value: 'recent30', text: 'Recognized in last 30 days' },
      ],
    }
  },
  computed: {
    recognitionRows() {
      const today = moment().startOf('day')
      return this.recognitionCandidates.map(rc => {
        const d = rc.driver
        const stat = this.ticketStatsByDriver[String(d.driver_id || '')]
          || this.ticketStatsByDriver[String(d.driver_name || '')] || {}
        const lastRecognized = stat.lastRecognized || null
        const days = lastRecognized ? today.diff(moment(lastRecognized.slice(0, 10)), 'days') : null
        return {
          key: rc.key,
          rc,
          driver_name: d.driver_name,
          score: d.compiled_score == null ? null : Math.round(d.compiled_score),
          tier: d.compiled_score_tier || '',
          miles: d.miles_driven == null ? null : Math.round(d.miles_driven),
          reasons: rc.reasons,
          reason_count: rc.reasons.length,
          last_recognized: lastRecognized,
          days_since_recognized: days,
          // "never recognized" sorts first under the default desc sort —
          // the most actionable gap on the page
          days_since_recognized_sort: days == null ? 100000 : days,
          queued: this.allReasonsQueued(rc),
        }
      })
    },
    // every recognition reason present this period, for the Reason dropdown
    reasonFilterOptions() {
      const labels = new Set()
      this.recognitionRows.forEach(r => r.reasons.forEach(x => labels.add(x.label)))
      return [
        { value: '', text: 'All reasons' },
        ...[...labels].sort().map(l => ({ value: l, text: l })),
      ]
    },
    // active filter dimensions for the mobile toggle badge (search excluded)
    activeFilterCount() {
      let n = 0
      if (this.recognitionReason) n++
      if (this.recognitionRecency) n++
      if (this.scorecardFrom || this.scorecardTo) n++
      return n
    },
    filtersDirty() {
      return this.recognitionSearch !== '' || this.recognitionReason !== ''
        || this.recognitionRecency !== ''
        || this.scorecardFrom !== '' || this.scorecardTo !== ''
    },
    recognitionRowsFiltered() {
      const search = this.recognitionSearch.trim().toLowerCase()
      return this.recognitionRows.filter(r => {
        if (this.recognitionReason && !r.reasons.some(x => x.label === this.recognitionReason)) return false
        if (this.recognitionRecency) {
          const days = r.days_since_recognized // null = never recognized
          if (this.recognitionRecency === 'never' && days != null) return false
          if (this.recognitionRecency === 'gap90' && days != null && days < 90) return false
          if (this.recognitionRecency === 'recent30' && (days == null || days >= 30)) return false
        }
        if (!search) return true
        return r.driver_name.toLowerCase().includes(search)
          || r.reasons.some(x => x.label.toLowerCase().includes(search))
      })
    },
    recognitionRowsPage() {
      const sorted = this.sortRows(this.recognitionRowsFiltered, this.recognitionSortBy, this.recognitionSortDesc)
      const start = (this.recognitionPage - 1) * this.perPage
      return sorted.slice(start, start + this.perPage)
    },
    recognitionFields() {
      return [
        { key: 'driver_name', label: 'Driver', sortable: true, thClass: 'cs-driver-cell', tdClass: 'cs-driver-cell' },
        { key: 'score', label: 'Score', sortable: true, thStyle: { width: '110px' } },
        { key: 'reason_count', label: 'Reasons', sortable: true },
        { key: 'miles', label: 'Miles', sortable: true, thStyle: { width: '86px' } },
        { key: 'days_since_recognized_sort', label: 'Last recognized', sortable: true, thStyle: { width: '136px' } },
        { key: 'actions', label: '', thStyle: { width: '118px' } },
      ]
    },
  },
  watch: {
    // filters reset pagination so a narrowed set never shows an empty page
    recognitionSearch() { this.recognitionPage = 1 },
    recognitionReason() { this.recognitionPage = 1 },
    recognitionRecency() { this.recognitionPage = 1 },
  },
  methods: {
    // a preset is "active" when the month pickers hold exactly its range —
    // no separate state to fall out of sync when a month is edited by hand
    periodPresetActive(days) {
      return this.scorecardFrom === moment().subtract(days, 'days').format('YYYY-MM')
        && this.scorecardTo === moment().format('YYYY-MM')
    },
    setPeriodPreset(days) {
      if (this.periodPresetActive(days)) {
        this.scorecardFrom = ''
        this.scorecardTo = ''
      } else {
        this.scorecardFrom = moment().subtract(days, 'days').format('YYYY-MM')
        this.scorecardTo = moment().format('YYYY-MM')
      }
      this.onPeriodChange()
    },
    // the scorecard window changed: re-query (the mixin swaps a backwards
    // pair rather than erroring)
    onPeriodChange() {
      this.recognitionPage = 1
      this.loadFlaggedDrivers()
    },
    clearFilters() {
      this.recognitionSearch = ''
      this.recognitionReason = ''
      this.recognitionRecency = ''
      this.recognitionPage = 1
      const hadPeriod = this.scorecardFrom || this.scorecardTo
      this.scorecardFrom = ''
      this.scorecardTo = ''
      if (hadPeriod) this.loadFlaggedDrivers()
    },
    onTenantScopeReset() {
      // the mixin already reset its own scorecardFrom/To
      this.recognitionSearch = ''
      this.recognitionReason = ''
      this.recognitionRecency = ''
      this.recognitionPage = 1
    },
  },
}
</script>

<style scoped>
/* the app shell centers text (#app) — this page is left-aligned, so inline
   controls (the mobile Filters toggle) sit left like everything else */
.cs-root { padding-bottom: 24px; text-align: left; }
.cs-stale { opacity: 0.6; }
.cs-small { font-size: 0.85em; }
.cs-hint { font-weight: 400; font-size: 0.85em; opacity: 0.7; }
.cs-positive { color: var(--fi-success, #2f7d32); }
.cs-tenant-note { margin-top: 10px; font-size: 0.85em; color: var(--fi-text-muted, #6b7280); }

.cs-panel-head {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  padding: 10px 14px; font-weight: 600; font-size: 0.95em;
  border-bottom: 1px solid var(--fi-line, #dee2e6);
}
.cs-panel-body { padding: 8px 14px; }

/* search/filter bar: the Coaching Tickets cp-filter-row recipe, cs-scoped */
.cs-filter-row {
  display: flex; flex-wrap: wrap; align-items: center;
  gap: 8px 12px;
  margin-top: 14px; padding-bottom: 8px;
  border-bottom: 1px solid #eef0f3;
  font-size: 13px;
}
.cs-filter-field { display: inline-flex; align-items: center; gap: 4px; }
.cs-filter-lbl {
  font-size: 10.5px; font-weight: 600;
  text-transform: uppercase; letter-spacing: .4px;
  color: #6b7280; white-space: nowrap;
}
.cs-filter-select { width: 180px; font-size: 13px; }
.cs-filter-select--narrow { width: 170px; }
.cs-filter-month { width: 150px; font-size: 13px; }
/* empty month input: placeholder-strength ink (safari renders the current
   month as ghost content, which reads as an active filter) */
.cs-filter-month.cs-month-ghost { color: #9aa4b1; }
.cs-filter-month.cs-month-ghost:focus { color: inherit; }
/* 7d/30d/90d one-tap presets (od-chip recipe, compact) */
.cs-preset-chip {
  background: #f8fafc; border: 1px solid #d6dde6; border-radius: 999px;
  padding: 3px 10px; font-size: 12px; font-weight: 600; color: #4b5563;
  cursor: pointer; transition: background .12s, border-color .12s, color .12s;
}
.cs-preset-chip + .cs-preset-chip { margin-left: 4px; }
.cs-preset-chip:hover { background: #eef4fb; border-color: #b8c8da; }
.cs-preset-chip--active {
  background: var(--fi-ops-accent, #3a6e99);
  border-color: var(--fi-ops-accent, #3a6e99);
  color: #fff;
}
.cs-search {
  margin-left: auto;
  position: relative;
  display: inline-flex; align-items: center;
  flex: 0 1 220px; min-width: 160px;
}
.cs-search-icon {
  position: absolute; left: 11px;
  color: #9aa3ad; font-size: 12px; pointer-events: none;
}
.cs-search-input {
  width: 100%;
  padding: 5px 26px 5px 28px;
  font-size: 13px;
  border: 1px solid var(--fi-line, #d6dde6); border-radius: 999px;
  background: var(--fi-surface, #fff); color: var(--fi-text, #1d1d1f);
  transition: border-color .12s, box-shadow .12s;
}
.cs-search-input::placeholder { color: #9aa3ad; }
.cs-search-input:focus {
  outline: none;
  border-color: var(--fi-ops-accent, #3a6e99);
  box-shadow: 0 0 0 2px rgba(58, 110, 153, 0.2);
}
.cs-search-clear {
  position: absolute; right: 6px;
  background: none; border: none; padding: 2px 5px;
  color: #9aa3ad; cursor: pointer; font-size: 12px; line-height: 1;
}
.cs-search-clear:hover { color: #4b5563; }
.cs-results-row {
  display: flex; align-items: center; gap: 10px;
  margin-top: 6px;
}
.cs-results-count { font-size: 12px; color: #6b7280; font-variant-numeric: tabular-nums; }
.cs-clear-filters {
  background: none; border: none; padding: 0;
  font-size: 12px; font-weight: 600; color: var(--fi-ops-accent-strong, #2d5a80);
  cursor: pointer;
}
.cs-clear-filters:hover { text-decoration: underline; }
.cs-pager {
  display: flex; align-items: center; gap: 10px;
  padding-top: 8px; margin-top: 2px;
  border-top: 1px solid var(--fi-line, #eee);
}
.cs-list-num { font-variant-numeric: tabular-nums; }
.cs-flag-chip {
  display: inline-block; margin: 1px 4px 1px 0;
  padding: 1px 7px;
  font-size: 11px; line-height: 1.5; font-weight: 600;
  border-radius: 999px; border: 1px solid;
  white-space: nowrap;
}
.cs-flag-chip--positive { background: #ddf3e3; color: #146230; border-color: #b7e2c4; }
.cs-label {
  font-size: 0.8em; font-weight: 600; text-transform: uppercase;
  letter-spacing: 0.04em; margin-bottom: 5px;
}

.cs-table-inline >>> .table-responsive { overflow-x: auto; overflow-y: hidden; }
.cs-table-inline >>> .table.b-table { font-size: 13px; border-collapse: collapse; }
.cs-table-inline >>> .table.b-table th,
.cs-table-inline >>> .table.b-table td {
  border-left: none !important; border-right: none !important;
  border-bottom: 1px solid #e0e0e0;
  padding: 3px 8px !important;
  vertical-align: middle !important;
  text-align: left !important;
}
.cs-table-inline >>> .table.b-table thead th {
  background: #f8f9fa;
  font-size: 12px; text-transform: uppercase; letter-spacing: 0.3px; color: #555;
  box-shadow: inset 0 -2px 0 0 #ccc;
}
.cs-table-inline >>> .table.b-table tbody tr:hover td { background-color: #f0f5ff; }
.cs-table-inline >>> .cs-driver-cell { white-space: nowrap; width: 1%; }
.cs-table-inline >>> th[aria-sort] { background-image: none !important; white-space: nowrap; }
.cs-table-inline >>> th[aria-sort]::before { display: none !important; }
.cs-table-inline >>> th[aria-sort]::after {
  display: inline-block !important; margin-left: 4px; font-size: 10px; color: #999;
  vertical-align: middle; float: none !important; width: auto !important;
  height: auto !important; opacity: 1 !important;
}
.cs-table-inline >>> th[aria-sort="none"]::after { content: "\2195" !important; }
.cs-table-inline >>> th[aria-sort="ascending"]::after { content: "\25B2" !important; color: var(--fi-rail-accent); }
.cs-table-inline >>> th[aria-sort="descending"]::after { content: "\25BC" !important; color: var(--fi-rail-accent); }

.cs-recognize-select { max-width: 260px; }

/* mobile filters toggle (the ERM Event Log pattern): hidden above 600px */
.cs-mobile-only { display: none; }
.cs-filter-toggle {
  align-items: center; gap: 7px;
  background: var(--fi-surface, #f8fafc);
  border: 1px solid var(--fi-line, #d6dde6); border-radius: 999px;
  padding: 7px 14px; margin: 14px 0 0;
  font-size: 13.5px; font-weight: 600; color: var(--fi-text, #374151);
  cursor: pointer; transition: background .12s, border-color .12s;
}
.cs-filter-toggle:focus-visible { outline: 2px solid var(--fi-ops-accent, #3a6e99); outline-offset: 2px; }
.cs-filter-badge {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 18px; height: 18px; padding: 0 5px;
  border-radius: 999px;
  background: var(--fi-ops-accent, #3a6e99); color: #fff;
  font-size: 11px; font-weight: 700; line-height: 1;
}
@media (max-width: 600px) {
  /* full-width search leading the filter row; 16px to dodge iOS focus-zoom */
  .cs-search { margin-left: 0; flex: 1 0 100%; order: -1; }
  .cs-search-input { font-size: 16px; }
  .cs-filter-field { flex: 1 1 auto; }
  .cs-filter-month, .cs-filter-select { width: 100%; font-size: 16px; }
  /* mobile-only affordances turn on here */
  .cs-mobile-only { display: inline-flex; }
  /* collapsed: every filter control hides except the search pill, so the
     phone opens straight to rows. the toggle above flips it open. */
  .cs-filters-collapsed .cs-filter-field { display: none; }
}

/* mobile: the header stays one row — title and actions inline. overrides
   FiPageRail's own 600px rule that wraps actions onto a full-width second
   row (fine for pages with a toolbar; a waste for one info icon). */
@media (max-width: 600px) {
  .cs-rail.fi-pr-rail { flex-wrap: nowrap; row-gap: 0; }
  .cs-rail >>> .fi-pr-actions { width: auto; }
}
</style>
